import { useRouter } from "@tanstack/react-router"
import {
  IconAlertWarningFill18,
  IconLockFill18,
  IconRefreshAnticlockwiseFill18,
} from "nucleo-ui-fill-18"
import type * as React from "react"

import { EmptyState } from "@/components/common/empty-state"
import { Button } from "@/components/ui/button"
import { isApiError, isNetworkError, toErrorMessage } from "@/lib/api/errors"

type RouteErrorProps = Omit<
  React.ComponentProps<typeof EmptyState>,
  "title" | "description" | "icon" | "actions"
> & {
  error: unknown
  reset: () => void
}

/**
 * What a page shows when it could not be loaded or drawn. A failure the API
 * explained keeps its message; anything else is a fault in this application,
 * whose message was written for developers, so it is not shown and the page is
 * reloaded rather than retried.
 */
function RouteError({ error, reset, ...props }: RouteErrorProps) {
  const router = useRouter()

  const retry = () => {
    reset()
    void router.invalidate()
  }

  if (isApiError(error) && error.isForbidden) {
    return (
      <EmptyState
        icon={<IconLockFill18 />}
        title="You don't have access to this"
        description={toErrorMessage(error)}
        {...props}
      />
    )
  }

  if (isApiError(error) || isNetworkError(error)) {
    return (
      <EmptyState
        icon={<IconAlertWarningFill18 />}
        title="This page couldn't be loaded"
        description={toErrorMessage(error)}
        actions={<RetryButton onClick={retry} />}
        {...props}
      />
    )
  }

  return (
    <EmptyState
      icon={<IconAlertWarningFill18 />}
      title="Something went wrong"
      description="This page couldn't be shown. Reloading usually fixes it."
      actions={<RetryButton onClick={() => window.location.reload()} />}
      {...props}
    />
  )
}

function RetryButton({ onClick }: { onClick: () => void }) {
  return (
    <Button variant="outline" size="sm" onClick={onClick}>
      <IconRefreshAnticlockwiseFill18 data-icon="inline-start" />
      Try again
    </Button>
  )
}

export { RouteError }
